import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Skeleton } from "../../Components/Needed";
import { EmptyState, StatusChip } from "../../Components/Kit";
import { fetchOverview } from "../../Helpers/notificationConsoleApi";
import RingNowDialog from "../Tasks/RingNowDialog";
import { whenText } from "./shared";

// Notification Centre > Overview (R6.2): what needs someone now, and how sending went over the last 7 days.
// Replaces the Health tab. Refreshes every minute.
const LEVEL = {
    urgent: { tone: "bad", label: "Urgent" },
    review: { tone: "wait", label: "Review" },
    fallback: { tone: "wait", label: "Fallback" },
    info: { tone: "info", label: "Info" },
};
const CHANNEL_NAME = { push: "Push", whatsapp: "WhatsApp", email: "Email", sms: "SMS" };
const STATE_TONE = { live: "ok", allowlist: "wait", shadow: "wait", off: "neutral", not_set_up: "neutral" };
const TYPE_NAME = { standard: "", carousel: "carousel", lto: "limited-time offer", auth: "passcode" };
const n = (value) => Number(value || 0).toLocaleString("en-IN");
const timeOf = (value) => new Date(value).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });

const Meter = ({ used, cap, tone }) => {
    const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
    return <div className={`nc-meter nc-meter-${tone}`} role="meter" aria-valuemin={0} aria-valuemax={cap || 0} aria-valuenow={used} aria-label={`${used} of ${cap}`}><div style={{ width: `${pct}%` }} /></div>;
};

const ActionButton = ({ item, openTab, navigate }) => {
    const a = item.action || {};
    if (a.kind === "ring") return null;
    if (a.kind === "reviews") return <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => navigate(`/reviews${a.filter ? `?filter=${a.filter}` : ""}`)}>Open reviews</button>;
    if (a.kind === "library") return <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => openTab("library", { libraryId: a.libraryId })}>Open template</button>;
    if (a.kind === "messages") return <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => openTab("messages", a.eventKey ? { eventKey: a.eventKey, audience: a.audience, channel: a.channel } : { filter: "attention" })}>{a.eventKey ? "Choose a template" : "See messages"}</button>;
    if (a.kind === "setup") return <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => openTab("setup", { sub: a.sub })}>Open setup</button>;
    if (a.kind === "delivery") return <button type="button" className="btn btn-link btn-sm" onClick={() => openTab("delivery", { filters: a.filters || {} })}>See deliveries</button>;
    return null;
};

const OverviewSection = ({ openTab }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [ringTaskId, setRingTaskId] = useState("");
    const navigate = useNavigate();

    const load = useCallback(async () => {
        const result = await fetchOverview();
        if (result.ok) setData(result.data); else toast.error(result.message);
        setLoading(false);
    }, []);
    useEffect(() => {
        load();
        const timer = setInterval(load, 60 * 1000);
        return () => clearInterval(timer);
    }, [load]);

    if (loading && !data) {
        return (
            <div className="nc-ov">
                <div className="nc-ov-stats">{[0, 1, 2, 3].map((i) => <Skeleton key={i} height={104} />)}</div>
                <Skeleton height={260} className="mt-3" />
            </div>
        );
    }
    if (!data) return <div className="settings-card"><EmptyState title="Could not load the overview" action={<button type="button" className="btn btn-outline-primary btn-sm" onClick={load}>Try again</button>} /></div>;

    const { numbers: num, attention, channels, waiting } = data;
    const fail = num.topFailure;

    return (
        <div className="nc-ov">
            <div className="nc-ov-stats">
                <div className="nc-ov-stat">
                    <div className="nc-ov-label">Sent, last 7 days</div>
                    <div className="nc-ov-value">{n(num.sent)}</div>
                    <div className={`nc-ov-sub ${num.deliveredRate !== null && num.deliveredRate < 90 ? "text-danger" : "text-success"}`}>{num.deliveredRate === null ? "Nothing sent yet" : `${num.deliveredRate}% delivered`}</div>
                </div>
                <div className="nc-ov-stat">
                    <div className="nc-ov-label">Failed</div>
                    <div className="nc-ov-value">{n(num.failed)}</div>
                    <div className="nc-ov-sub">{fail ? `Mostly ${CHANNEL_NAME[fail.channel] || fail.channel}: ${fail.code} (${n(fail.count)})` : "No failures"}{num.skipped ? ` · ${n(num.skipped)} skipped` : ""}</div>
                </div>
                <div className="nc-ov-stat">
                    <div className="nc-ov-label">WhatsApp today</div>
                    <div className="nc-ov-value">{n(num.whatsappToday.sent)} <span className="nc-ov-cap">/ {n(num.whatsappToday.cap)}</span></div>
                    <Meter used={num.whatsappToday.sent} cap={num.whatsappToday.cap} tone="primary" />
                </div>
                <div className="nc-ov-stat">
                    <div className="nc-ov-label">Email today</div>
                    <div className="nc-ov-value">{n(num.emailToday.sent)} <span className="nc-ov-cap">/ {n(num.emailToday.cap)}</span></div>
                    <Meter used={num.emailToday.sent} cap={num.emailToday.cap} tone="accent" />
                </div>
            </div>

            <div className="nc-ov-grid">
                <section className="nc-ov-card nc-ov-attention" aria-labelledby="nc-att-title">
                    <div className="nc-ov-card-head">
                        <h2 id="nc-att-title" className="nc-ov-h">Needs attention</h2>
                        {attention.length ? <StatusChip tone="bad">{attention.length} open</StatusChip> : <StatusChip tone="ok">All clear</StatusChip>}
                    </div>
                    {attention.length === 0 ? <div className="nc-ov-clear">Nothing needs you right now. Messages are going out as usual.</div> : attention.map((item) => {
                        const level = LEVEL[item.level] || LEVEL.info;
                        return (
                            <div key={item.key} className="nc-ov-item">
                                <StatusChip tone={level.tone}>{level.label}</StatusChip>
                                <div className="nc-ov-item-text">
                                    <div className="nc-ov-item-title">{item.title}</div>
                                    {item.detail ? <div className="nc-ov-sub">{item.detail}</div> : null}
                                    {item.action?.kind === "ring" ? (
                                        <ul className="nc-ov-tasks">
                                            {item.action.tasks.map((t) => (
                                                <li key={t.taskId}>
                                                    <span><strong>{t.bookingId || t.taskId}</strong> {t.typeText.toLowerCase()} {timeOf(t.at)}{t.hub ? ` · ${t.hub}` : ""}{t.late ? <span className="text-danger fw-semibold"> · late</span> : null}</span>
                                                    <button type="button" className="btn btn-primary btn-sm" onClick={() => setRingTaskId(t.taskId)}>Ring now</button>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : null}
                                </div>
                                <div className="nc-ov-item-action"><ActionButton item={item} openTab={openTab} navigate={navigate} /></div>
                            </div>
                        );
                    })}
                </section>

                <div className="nc-ov-side">
                    <section className="nc-ov-card" aria-labelledby="nc-ch-title">
                        <h2 id="nc-ch-title" className="nc-ov-h mb-2">Channels</h2>
                        {channels.map((c) => (
                            <div key={c.name} className="nc-ov-row">
                                <span className="fw-semibold">{CHANNEL_NAME[c.name]}</span>
                                <StatusChip tone={STATE_TONE[c.state] || "neutral"}>{c.label}</StatusChip>
                            </div>
                        ))}
                        <button type="button" className="btn btn-link btn-sm px-0 mt-1" onClick={() => openTab("setup", { sub: "whatsapp" })}>Channel setup</button>
                    </section>
                    <section className="nc-ov-card" aria-labelledby="nc-wait-title">
                        <h2 id="nc-wait-title" className="nc-ov-h mb-2">Waiting for Meta</h2>
                        {waiting.length === 0 ? <div className="nc-ov-sub">No WhatsApp template is in review.</div> : waiting.map((w) => (
                            <button key={w.libraryId} type="button" className="nc-ov-row nc-ov-link" onClick={() => openTab("library", { libraryId: w.libraryId })}>
                                <span>{w.name}{TYPE_NAME[w.type] ? <span className="nc-ov-sub"> ({TYPE_NAME[w.type]})</span> : null}</span>
                                <StatusChip tone="wait">{w.status === "IN_APPEAL" ? "Appeal" : "In review"}</StatusChip>
                            </button>
                        ))}
                    </section>
                    <section className="nc-ov-card" aria-labelledby="nc-rev-title">
                        <h2 id="nc-rev-title" className="nc-ov-h mb-2">Reviews this week</h2>
                        {data.reviews?.count ? (
                            <>
                                <div className="d-flex align-items-baseline gap-2"><span className="nc-ov-value">{data.reviews.average}</span><span className="nc-ov-sub">average from {data.reviews.count} review{data.reviews.count === 1 ? "" : "s"}</span></div>
                                {data.reviews.poor ? <div className="nc-ov-sub text-danger fw-bold mt-1">{data.reviews.poor} poor (1-2 stars)</div> : null}
                            </>
                        ) : <div className="nc-ov-sub">No reviews this week yet.</div>}
                        <button type="button" className="btn btn-link btn-sm px-0 mt-1" onClick={() => navigate("/reviews")}>All reviews</button>
                    </section>
                    <section className="nc-ov-card" aria-labelledby="nc-by-title">
                        <h2 id="nc-by-title" className="nc-ov-h mb-2">Last 7 days by channel</h2>
                        <table className="nc-ov-table">
                            <thead><tr><th scope="col">Channel</th><th scope="col">Sent</th><th scope="col">Failed</th><th scope="col">Delivered</th></tr></thead>
                            <tbody>
                                {Object.entries(num.byChannel).map(([name, c]) => (
                                    <tr key={name}>
                                        <th scope="row">{CHANNEL_NAME[name]}</th>
                                        <td>{n(c.sent)}</td>
                                        <td className={c.failed ? "text-danger" : ""}>{n(c.failed)}</td>
                                        <td>{c.deliveredRate === null ? "-" : `${c.deliveredRate}%`}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="nc-ov-sub mt-2">Since {whenText(num.since)}. Updated {timeOf(data.at)}.</div>
                    </section>
                </div>
            </div>

            {ringTaskId ? <RingNowDialog taskId={ringTaskId} statusUrl={`/admin/task/${ringTaskId}/ring`} ringUrl={`/admin/task/${ringTaskId}/ring`} onClose={() => setRingTaskId("")} onChanged={load} /> : null}
        </div>
    );
};

export default OverviewSection;
